import { analyzeDungeonPlan } from './analyze.js';
import { generateBossEncounter, generateOrdinaryEncounter, selectBosses, selectMutators, } from './encounters.js';
import { fingerprintDungeonMaterial } from './fingerprint.js';
import { validateDungeonLayout } from './layout.js';
import { generateDungeonLayout, layoutRulesFor } from './layout-stage.js';
import { normaliseSeed, SeededRandom } from './random.js';
import { generateTopology } from './topology.js';
import { DUNGEON_GENERATOR_VERSION, DUNGEON_PLAN_SCHEMA_VERSION, DungeonGenerationError, } from './types.js';
import { profileById, themeById, validateDungeonCatalog, validateDungeonPlan, validateGenerateDungeonRequest, } from './validate.js';
function generationProblem(code, path, message) {
    return { code, path, message };
}
function attachEncounters(planRooms, bossRoomIds, request, catalog, profile, theme, mutators, random) {
    const bosses = selectBosses(catalog, theme, request.targetTier, bossRoomIds.length, request.history, random);
    const bossByRoom = new Map(bossRoomIds.map((roomId, index) => [roomId, bosses[index]]));
    const maximumDepth = Math.max(...planRooms.map((room) => room.depth));
    const runEnemyCounts = new Map();
    let encounterIndex = 0;
    for (const room of planRooms) {
        if (room.role === 'combat') {
            room.encounter = generateOrdinaryEncounter({
                room,
                encounterIndex,
                catalog,
                profile,
                theme,
                targetTier: request.targetTier,
                partySize: request.partySize,
                maximumDepth,
                mutators,
                history: request.history,
                runEnemyCounts,
                random,
            });
            encounterIndex += 1;
            continue;
        }
        if (room.role === 'miniboss' || room.role === 'boss') {
            const boss = bossByRoom.get(room.id);
            if (!boss)
                throw new Error(`no boss selected for room '${room.id}'`);
            room.encounter = generateBossEncounter(encounterIndex, boss, profile, mutators);
            encounterIndex += 1;
            runEnemyCounts.set(boss.id, (runEnemyCounts.get(boss.id) ?? 0) + 1);
        }
    }
}
function buildObjectives(planRooms, criticalPathRoomIds, finalBossRoomId, exitRoomId) {
    const roomById = new Map(planRooms.map((room) => [room.id, room]));
    const objectives = [];
    let lastRequiredObjectiveId;
    let finalBossObjectiveId;
    const addObjective = (roomId, kind, required, dependsOn, encounterId) => {
        const objective = {
            id: `objective-${String(objectives.length).padStart(3, '0')}`,
            kind,
            roomId,
            required,
            dependsOn,
            ...(encounterId ? { encounterId } : {}),
        };
        objectives.push(objective);
        roomById.get(roomId)?.objectiveIds.push(objective.id);
        return objective;
    };
    for (const roomId of criticalPathRoomIds) {
        if (roomId === exitRoomId)
            continue;
        const room = roomById.get(roomId);
        if (!room?.encounter)
            continue;
        const kind = room.encounter.kind === 'boss' ? 'defeat-boss' : 'clear-room';
        const objective = addObjective(room.id, kind, true, lastRequiredObjectiveId ? [lastRequiredObjectiveId] : [], room.encounter.id);
        lastRequiredObjectiveId = objective.id;
        if (room.id === finalBossRoomId)
            finalBossObjectiveId = objective.id;
    }
    for (const room of planRooms) {
        if (room.criticalPath)
            continue;
        if (room.encounter) {
            addObjective(room.id, 'clear-room', false, [], room.encounter.id);
        }
        else if (room.role === 'treasure') {
            addObjective(room.id, 'discover-room', false, []);
        }
    }
    if (!finalBossObjectiveId)
        throw new Error('final boss room did not produce an objective');
    const completion = addObjective(exitRoomId, 'complete-dungeon', true, [finalBossObjectiveId]);
    return {
        objectives,
        finalBossObjectiveId,
        completionObjectiveId: completion.id,
    };
}
function buildRewardIntents(rooms, bossRoomIds, finalBossObjectiveId, completionObjectiveId, rewardClass) {
    const intents = [];
    for (const roomId of bossRoomIds) {
        intents.push({
            id: `reward-intent-${String(intents.length).padStart(3, '0')}`,
            source: 'boss',
            rewardClass: `${rewardClass}:boss`,
            relatedRoomId: roomId,
            unlockObjectiveIds: [finalBossObjectiveId],
            authority: 'existing-reward-services',
        });
    }
    for (const room of rooms.filter((candidate) => candidate.role === 'treasure')) {
        const discoveryObjective = room.objectiveIds[0];
        if (!discoveryObjective)
            continue;
        intents.push({
            id: `reward-intent-${String(intents.length).padStart(3, '0')}`,
            source: 'optional',
            rewardClass: `${rewardClass}:optional`,
            relatedRoomId: room.id,
            unlockObjectiveIds: [discoveryObjective, finalBossObjectiveId],
            authority: 'existing-reward-services',
        });
    }
    const exitRoom = rooms.find((room) => room.role === 'exit');
    if (!exitRoom)
        throw new Error('exit room missing while building completion reward');
    intents.push({
        id: `reward-intent-${String(intents.length).padStart(3, '0')}`,
        source: 'completion',
        rewardClass,
        relatedRoomId: exitRoom.id,
        unlockObjectiveIds: [finalBossObjectiveId, completionObjectiveId],
        authority: 'existing-reward-services',
    });
    return intents;
}
function buildCandidate(request, catalog, attempt) {
    const profile = profileById(catalog, request.difficultyId);
    const theme = themeById(catalog, request.themeId);
    if (!profile || !theme)
        throw new Error('validated request lost its profile or theme');
    const seed = normaliseSeed(request.seed);
    const effectiveSeed = attempt === 0 ? seed : `${seed}::retry:${attempt}`;
    const random = new SeededRandom(effectiveSeed);
    const mutators = selectMutators(catalog, profile, theme, request.history, random);
    const roomCount = request.roomCount ?? random.int(profile.roomCount.min, profile.roomCount.max);
    const bossCount = request.bossCount ?? profile.bossCount;
    const topology = generateTopology({
        roomCount,
        bossCount,
        profile,
        theme,
        mutators,
        random,
    });
    attachEncounters(topology.rooms, topology.bossRoomIds, request, catalog, profile, theme, mutators, random);
    const objectiveResult = buildObjectives(topology.rooms, topology.criticalPathRoomIds, topology.finalBossRoomId, topology.exitRoomId);
    const finalGate = topology.connections.find((connection) => [connection.a, connection.b].includes(topology.finalBossRoomId) &&
        [connection.a, connection.b].includes(topology.exitRoomId));
    if (!finalGate)
        throw new Error('topology omitted the boss-to-exit connection');
    finalGate.lockedByObjectiveId = objectiveResult.finalBossObjectiveId;
    const rewardIntents = buildRewardIntents(topology.rooms, topology.bossRoomIds, objectiveResult.finalBossObjectiveId, objectiveResult.completionObjectiveId, profile.rewardClass);
    const metrics = analyzeDungeonPlan(topology.rooms, topology.connections, topology.criticalPathRoomIds);
    // AD-404 S2: the layout stage runs after encounters (it sizes chambers from
    // them) on its own stream, so layout draws never perturb the topology.
    const layout = generateDungeonLayout({
        rooms: topology.rooms,
        connections: topology.connections,
        criticalPathRoomIds: topology.criticalPathRoomIds,
        entryRoomId: topology.entryRoomId,
        partySize: request.partySize,
        profile,
        theme,
        rules: layoutRulesFor(catalog, profile, theme),
        random: new SeededRandom(`${effectiveSeed}::layout`),
    });
    const material = {
        schemaVersion: DUNGEON_PLAN_SCHEMA_VERSION,
        generatorVersion: DUNGEON_GENERATOR_VERSION,
        catalogVersion: catalog.version,
        seed,
        effectiveSeed,
        generationAttempt: attempt,
        difficultyId: profile.id,
        themeId: theme.id,
        targetTier: request.targetTier,
        partySize: request.partySize,
        roomSpacing: theme.roomSpacing,
        mutatorIds: mutators.map((mutator) => mutator.id),
        rooms: topology.rooms,
        connections: topology.connections,
        entryRoomId: topology.entryRoomId,
        exitRoomId: topology.exitRoomId,
        finalBossRoomId: topology.finalBossRoomId,
        bossRoomIds: topology.bossRoomIds,
        criticalPathRoomIds: topology.criticalPathRoomIds,
        objectives: objectiveResult.objectives,
        rewardIntents,
        metrics,
        layout,
    };
    const fingerprint = fingerprintDungeonMaterial(material);
    return {
        id: `dungeon-${fingerprint.slice(0, 12)}`,
        ...material,
        fingerprint,
    };
}
export function generateDungeon(request, catalog) {
    const catalogProblems = validateDungeonCatalog(catalog);
    if (catalogProblems.length > 0)
        throw new DungeonGenerationError(catalogProblems);
    const requestProblems = validateGenerateDungeonRequest(request, catalog);
    if (requestProblems.length > 0)
        throw new DungeonGenerationError(requestProblems);
    const maxAttempts = request.maxAttempts ?? 8;
    const recentFingerprints = new Set(request.history?.recentFingerprints ?? []);
    let lastProblems = [];
    let fingerprintCollisionCount = 0;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
            const candidate = buildCandidate(request, catalog, attempt);
            const planProblems = validateDungeonPlan(candidate, catalog);
            if (planProblems.length > 0) {
                lastProblems = planProblems;
                continue;
            }
            const layoutProblems = validateDungeonLayout(candidate, layoutRulesFor(catalog, profileById(catalog, request.difficultyId), themeById(catalog, request.themeId)));
            if (layoutProblems.length > 0) {
                lastProblems = layoutProblems;
                continue;
            }
            if (recentFingerprints.has(candidate.fingerprint)) {
                fingerprintCollisionCount += 1;
                continue;
            }
            return candidate;
        }
        catch (error) {
            lastProblems = [
                generationProblem('candidate-generation-failed', `generation.attempt[${attempt}]`, error instanceof Error ? error.message : String(error)),
            ];
        }
    }
    if (fingerprintCollisionCount === maxAttempts) {
        throw new DungeonGenerationError([
            generationProblem('freshness-exhausted', 'request.history.recentFingerprints', `all ${maxAttempts} deterministic candidates appeared in recent history`),
        ]);
    }
    throw new DungeonGenerationError(lastProblems.length > 0
        ? lastProblems
        : [
            generationProblem('generation-exhausted', 'generation', 'no valid candidate was produced'),
        ]);
}
export function generateDungeonSafe(request, catalog) {
    try {
        return { ok: true, plan: generateDungeon(request, catalog) };
    }
    catch (error) {
        if (error instanceof DungeonGenerationError)
            return { ok: false, problems: error.problems };
        return {
            ok: false,
            problems: [
                generationProblem('unexpected-generation-error', 'generation', error instanceof Error ? error.message : String(error)),
            ],
        };
    }
}
//# sourceMappingURL=generate.js.map